import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { TaskStatusBadge } from '@/components/fleet/task-status-badge';
import { TierBadge } from '@/components/fleet/tier-badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTaskHistory } from '@/hooks/use-task-history';
import type { ProvisioningTaskStatus } from '@/types/provisioning';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: Array<{ value: ProvisioningTaskStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'stopped', label: 'Stopped' },
];

/**
 * The task-history audit table — who ran what, when, with what result.
 * Paginated and filterable by status. Clicking a row opens the task drawer.
 */
export function TaskHistory({
  onSelectTask,
}: {
  onSelectTask: (taskId: number, templateName: string) => void;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ProvisioningTaskStatus | 'all'>('all');

  const query = useTaskHistory({
    page,
    page_size: PAGE_SIZE,
    status: status === 'all' ? undefined : status,
  });

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as ProvisioningTaskStatus | 'all');
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {query.isFetching && (
          <span className="text-xs text-muted-foreground">refreshing…</span>
        )}
      </div>

      {query.isLoading && <TableSkeleton rows={6} />}

      {query.isError && !query.data && (
        <ErrorState
          title="Couldn't load task history."
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.items.length === 0 && (
        <EmptyState message="No tasks match this filter." />
      )}

      {query.data && query.data.items.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Playbook</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.items.map((task) => (
                <TableRow
                  key={task.task_id}
                  className="cursor-pointer"
                  onClick={() => onSelectTask(task.task_id, task.template.name)}
                >
                  <TableCell>
                    <TaskStatusBadge status={task.status} />
                  </TableCell>
                  <TableCell>{task.template.name}</TableCell>
                  <TableCell>
                    <TierBadge tier={task.template.tier} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{task.actor}</TableCell>
                  <TableCell>
                    <span title={format(new Date(task.created_at), 'yyyy-MM-dd HH:mm:ss')}>
                      {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {total} task{total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground tabular-nums">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
